import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import CardGridControl from "../../CardGridControl";
import {
  buildDeploymentFormMetadata,
  buildDeploymentStepValues,
  buildDestinationDeploymentSavePayload,
  buildDestinationConnectionCheckMetadata,
  applyEndpointClientRouteContext,
  createClientGuid,
  createDeploymentDraft,
  getDeploymentCollectionField,
  getDeploymentCardTitle,
  getDeploymentItemName,
  getDeploymentLabel,
  getDeploymentDisplayName,
  getDeploymentRecordId,
  getDestinationCheckOperationName,
  getDestinationDeploymentStepDescription,
  getDestinationDeploymentRows,
  getDestinationIdFromValues,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationRequestOnlyReason,
  getDestinationSetupDefaults,
  getSavedDeploymentStepValues,
  getDestinationRowKey,
  getEntityDeploymentRows,
  getEntityObjectDisplayName,
  getEntityObjectRecordId,
  hasLoadedManagerMetadata,
  isRequestOnlyDestinationSetup,
  isCoreProfileMetadataReady,
  isSchemaAPISetup,
  loadDestinationById,
  loadDestinationManagerMetadata,
  normalizeEntityDeploymentRow,
  normalizeDeploymentRow,
  resolveEndpointClientShortNameAsync,
  resolveMetadataItem,
  saveDestination,
  setEntityDeploymentRows,
  validateDeploymentRows
} from "./destinationSetupStepUtils";
import {
  asArray,
  buildConnectionCheckFailureStatus,
  buildResourceOperationQueryParams,
  buildServerFailureError,
  buildWizardValidationError,
  getErrorSummary,
  getMetadataFields,
  getOperationItem,
  getOperationItems,
  getOperationResult,
  readValue,
  requireSuccessfulServiceResponse,
  requireServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const ENTITY_SELECTOR_METADATA = {
  Fields: [
    { FieldName: "EntitySelectorName", DisplayName: "Entity Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "EntitySelectorObject", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "EntitySelectorSourceObjectOfRecord", DisplayName: "Source Object of Record", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 }
  ]
};

const normalizeKey = (value) => String(value || "").trim();
const entityDeploymentPairKey = (row) =>
  `${normalizeKey(readValue(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId"))}:${normalizeKey(readValue(row, "EntityObjectId", "entityObjectId"))}`;
const getEndpointEntityRows = (deployment) =>
  asArray(readValue(deployment, "EntityEndpoints", "entityEndpoints"));
const endpointEntityPairKey = (deploymentId, row) =>
  `${normalizeKey(deploymentId)}:${normalizeKey(readValue(row, "EntityObjectId", "entityObjectId"))}`;
const getDefaultDeployment = (deployments = []) =>
  asArray(deployments).find((row) => readValue(row, "IsDeleted", "isDeleted") !== true && readValue(row, "IsActive", "isActive") !== false)
  || asArray(deployments).find((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
  || asArray(deployments)[0]
  || null;
const isEndpointMcpSurfaceEnabled = (deployment) => {
  const publicSurfaceMode = Number(readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  return publicSurfaceMode === 2 || publicSurfaceMode === 3;
};
const getNormalizedFieldName = (field) => String(readValue(field, "FieldName", "fieldName") || "").trim().toLowerCase();
const parseRuntimeCapacityGrantSelection = (value) => {
  const rawValue = value && typeof value === "object" && !Array.isArray(value)
    ? readValue(value, "Value", "value", "Text", "text") ?? ""
    : value ?? "";
  const selectedValue = String(rawValue).trim();
  const [clientProductOfferId = "", capacityGrantKey = ""] = selectedValue.split("|");
  return {
    selectedValue,
    clientProductOfferId: clientProductOfferId.trim(),
    capacityGrantKey: capacityGrantKey.trim(),
    displayName: value && typeof value === "object" && !Array.isArray(value)
      ? readValue(value, "Text", "text", "DisplayName", "displayName") || ""
      : ""
  };
};
const normalizeEndpointRuntimeCapacityUpdate = (nextDeployment, previousDeployment) => {
  const nextGrant = readValue(nextDeployment, "RuntimeCapacityClientProductOfferId", "runtimeCapacityClientProductOfferId") || "";
  const previousGrant = readValue(previousDeployment, "RuntimeCapacityClientProductOfferId", "runtimeCapacityClientProductOfferId") || "";
  if (String(nextGrant) === String(previousGrant)) return nextDeployment;

  const selection = parseRuntimeCapacityGrantSelection(nextGrant);
  return {
    ...nextDeployment,
    RuntimeCapacityClientProductOfferId: selection.selectedValue,
    RuntimeCapacityGrantKey: selection.capacityGrantKey,
    RuntimeCapacityGrantDisplayName: selection.displayName,
    RuntimeCapacityProductOfferKey: "",
    RuntimeCapacityProductOfferSku: "",
    RuntimeCapacityPoolType: null,
    RuntimeCapacityIsUnlimited: false,
    RuntimeCapacityPoolProfileId: "",
    BatchPoolProfileId: null
  };
};
export const normalizeEntityObjectSelectionKeys = (nextKeys = []) =>
  asArray(nextKeys).map(normalizeKey).filter(Boolean);
const SOURCE_OBJECT_SELECT_DATA_FIELD = {
  FieldName: "SourceObjectOfRecordId",
  DisplayName: "Source Object of Record",
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataOperationItem: "SourceObject"
};
const getEntityObjectSourceObjectOfRecordId = (entityObject) =>
  normalizeKey(readValue(entityObject, "SourceObjectOfRecordId", "sourceObjectOfRecordId", "SourceObjectId", "sourceObjectId"));
const getSourceObjectDisplayName = (sourceObject) =>
  readValue(sourceObject, "DisplayName", "displayName", "Text", "text", "Name", "name", "SystemName", "systemName", "ObjectName", "objectName") || "";
const getSourceObjectRecordIds = (sourceObject) => [
  readValue(sourceObject, "_id", "id"),
  readValue(sourceObject, "Value", "value"),
  readValue(sourceObject, "UniqueId", "uniqueId"),
  readValue(sourceObject, "SourceObjectId", "sourceObjectId", "EntityObjectId", "entityObjectId")
].map(normalizeKey).filter(Boolean);
const buildSourceObjectsById = (sourceObjects = []) => {
  const sourceObjectsById = new Map();
  asArray(sourceObjects).forEach((sourceObject) => {
    const displayName = getSourceObjectDisplayName(sourceObject);
    if (!displayName) return;
    getSourceObjectRecordIds(sourceObject).forEach((id) => sourceObjectsById.set(id.toLowerCase(), displayName));
  });
  return sourceObjectsById;
};
const getEntityObjectSourceObjectOfRecordDisplayName = (entityObject, sourceObjectsById = new Map()) => {
  const directName = readValue(
    entityObject,
    "SourceObjectOfRecordDisplayName",
    "sourceObjectOfRecordDisplayName",
    "SourceObjectOfRecordName",
    "sourceObjectOfRecordName",
    "SourceObjectDisplayName",
    "sourceObjectDisplayName",
    "SourceObjectName",
    "sourceObjectName"
  );
  if (directName) return directName;
  const sourceObjectId = getEntityObjectSourceObjectOfRecordId(entityObject);
  return sourceObjectId ? sourceObjectsById.get(sourceObjectId.toLowerCase()) || "" : "";
};
const loadSourceObjectSelectData = async (sourceObjectMgr) => {
  if (!sourceObjectMgr?.GetSelectData) return [];
  const result = await sourceObjectMgr.GetSelectData(SOURCE_OBJECT_SELECT_DATA_FIELD);
  return asArray(result?.jsonData);
};
const getEntitySelectorHeaderStatus = (row) => {
  const active = readValue(row, "IsActive", "isActive") !== false;
  return {
    title: active ? "Active" : "Inactive",
    tone: active ? "active" : "inactive"
  };
};

const DestinationDeploymentConfiguratorStep = ({
  context,
  onChange,
  onSave,
  onContinue,
  registerStepController,
  onDirty
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults),
    [context.productResourceDefaults, wizard]
  );
  const isEndpointSetup = isSchemaAPISetup(productResourceDefaults);
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const destinationMgr = resourceDomainContext?.managers?.[managerKey];
  const clientMgr = resourceDomainContext?.managers?.client;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const sourceObjectMgr = resourceDomainContext?.managers?.sourceObject;
  const savedValues = getSavedDeploymentStepValues(wizard, values);
  const destinationId = getDestinationIdFromValues(wizard, savedValues, productResourceDefaults);
  const destinationLabel = getDestinationLabel(productResourceDefaults);
  const deploymentLabel = getDeploymentLabel(productResourceDefaults);
  const deploymentItemName = getDeploymentItemName(productResourceDefaults);
  const checkOperationName = getDestinationCheckOperationName(productResourceDefaults);
  const requestOnlySetup = isRequestOnlyDestinationSetup(productResourceDefaults);
  const requestOnlyReason = getDestinationRequestOnlyReason(productResourceDefaults);
  const profileMetadataReady = isCoreProfileMetadataReady(coreContext);
  const [destination, setDestination] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(Boolean(destinationId));
  const [localError, setLocalError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState(() => ({
    Status: readValue(values, "DestinationConnectionCheckStatus", "ConnectionCheckStatus") || "",
    Message: readValue(values, "DestinationConnectionCheckMessage", "ConnectionCheckMessage") || "",
    CheckedUtc: readValue(values, "DestinationConnectionCheckedUtc", "ConnectionCheckedUtc") || "",
    DeploymentId: readValue(values, "DestinationConnectionCheckDeploymentId", "ConnectionCheckDeploymentId") || "",
    DeploymentName: readValue(values, "DestinationConnectionCheckDeploymentName", "ConnectionCheckDeploymentName") || ""
  }));
  const [checkingDeploymentKey, setCheckingDeploymentKey] = useState("");
  const [selectedDeploymentKey, setSelectedDeploymentKey] = useState("");
  const [cardEditorOpen, setCardEditorOpen] = useState(false);
  const [entityObjects, setEntityObjects] = useState([]);
  const [sourceObjectsById, setSourceObjectsById] = useState(() => new Map());
  const [entitySelectorOpen, setEntitySelectorOpen] = useState(false);
  const [selectedEntityObjectIds, setSelectedEntityObjectIds] = useState([]);
  const [entitySelectorLoading, setEntitySelectorLoading] = useState(false);
  const destinationMgrRef = useRef(destinationMgr);
  const clientMgrRef = useRef(clientMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const coreContextRef = useRef(coreContext);
  const coreStateRef = useRef(coreState);
  const destinationRef = useRef(destination);
  const deploymentsRef = useRef(deployments);
  const entityObjectsRef = useRef(entityObjects);
  const productResourceDefaultsRef = useRef(productResourceDefaults);
  const loadKeyRef = useRef("");
  const draftSequenceRef = useRef(0);
  const pendingDeploymentKeysRef = useRef(new Set());
  const isBusyRef = useRef(false);
  const connectionStatusRef = useRef(connectionStatus);
  const entitySelectorResolverRef = useRef(null);
  const pendingEntityScopeSelectionRef = useRef(null);
  const preparedDeploymentSaveRef = useRef(null);
  const selectedEntityObjectIdsRef = useRef(selectedEntityObjectIds);

  destinationMgrRef.current = destinationMgr;
  clientMgrRef.current = clientMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  sourceObjectMgrRef.current = sourceObjectMgr;
  coreContextRef.current = coreContext;
  coreStateRef.current = coreState;
  destinationRef.current = destination;
  deploymentsRef.current = deployments;
  entityObjectsRef.current = entityObjects;
  productResourceDefaultsRef.current = productResourceDefaults;
  connectionStatusRef.current = connectionStatus;
  selectedEntityObjectIdsRef.current = selectedEntityObjectIds;
  isBusyRef.current = loading || Boolean(checkingDeploymentKey) || entitySelectorLoading;

  useEffect(() => {
    setConnectionStatus({
      Status: readValue(values, "DestinationConnectionCheckStatus", "ConnectionCheckStatus") || "",
      Message: readValue(values, "DestinationConnectionCheckMessage", "ConnectionCheckMessage") || "",
      CheckedUtc: readValue(values, "DestinationConnectionCheckedUtc", "ConnectionCheckedUtc") || "",
      DeploymentId: readValue(values, "DestinationConnectionCheckDeploymentId", "ConnectionCheckDeploymentId") || "",
      DeploymentName: readValue(values, "DestinationConnectionCheckDeploymentName", "ConnectionCheckDeploymentName") || ""
    });
  }, [values]);

  const updateConnectionStatus = useCallback((status) => {
    setConnectionStatus(status);
    onChange?.("DestinationConnectionCheckStatus", status.Status || "");
    onChange?.("DestinationConnectionCheckMessage", status.Message || "");
    onChange?.("DestinationConnectionCheckedUtc", status.CheckedUtc || "");
    onChange?.("DestinationConnectionCheckDeploymentId", status.DeploymentId || "");
    onChange?.("DestinationConnectionCheckDeploymentName", status.DeploymentName || "");
  }, [onChange]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = destinationMgrRef.current;
    const loadKey = `${managerKey}:${destinationId || "none"}:${deploymentItemName}`;
    if (!destinationId) {
      setDestination(null);
      setDeployments([]);
      setLoading(false);
      setLocalError("");
      return undefined;
    }
    if (!activeMgr) {
      setLoading(false);
      setLocalError(`${destinationLabel} service is not available.`);
      return undefined;
    }
    if (!hasLoadedManagerMetadata(activeMgr) && !isCoreProfileMetadataReady(coreContextRef.current)) {
      setLoading(true);
      setLocalError("");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        const [loadedDestination, metadataResult] = await Promise.all([
          loadDestinationById({
            manager: activeMgr,
            destinationId,
            coreState: coreStateRef.current,
            fallbackMessage: `${destinationLabel} could not be loaded.`
          }),
          loadDestinationManagerMetadata({
            manager: activeMgr,
            coreContext: coreContextRef.current
          })
        ]);
        if (!mounted) return;
        requireServiceResponse(metadataResult, `${deploymentLabel} metadata could not be loaded.`);
        if (!loadedDestination) throw new Error(`The linked ${destinationLabel} could not be loaded.`);
        let routeContextDestination = applyEndpointClientRouteContext(loadedDestination, {
          defaults: productResourceDefaultsRef.current,
          coreContext: coreContextRef.current
        });
        if (isSchemaAPISetup(productResourceDefaultsRef.current)
          && !readValue(routeContextDestination, "ClientShortName", "clientShortName")) {
          const resolvedClientShortName = await resolveEndpointClientShortNameAsync({
            destination: routeContextDestination,
            clientMgr: clientMgrRef.current
          });
          if (resolvedClientShortName) {
            routeContextDestination = {
              ...routeContextDestination,
              ClientShortName: resolvedClientShortName
            };
          }
        }
        const resolvedMetadata = resolveMetadataItem(metadataResult, deploymentItemName)
          || resolveMetadataItem(activeMgr.uiMetadata, deploymentItemName);
        const loadedDeployments = getDestinationDeploymentRows(routeContextDestination, productResourceDefaultsRef.current)
          .map((deployment) => normalizeDeploymentRow(deployment, routeContextDestination, productResourceDefaultsRef.current));
        const rowsToEdit = loadedDeployments.length
          ? loadedDeployments
          : [createDeploymentDraft(routeContextDestination, productResourceDefaultsRef.current, 1)];
        setMetadata(resolvedMetadata);
        setDestination(routeContextDestination);
        pendingDeploymentKeysRef.current.clear();
        if (!loadedDeployments.length) {
          draftSequenceRef.current = Math.max(draftSequenceRef.current, 1);
          pendingDeploymentKeysRef.current.add(String(getDestinationRowKey(rowsToEdit[0])));
        }
        setDeployments(rowsToEdit);
        setSelectedDeploymentKey("");
        setCardEditorOpen(false);
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || `${deploymentLabel} records could not be loaded.`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [deploymentItemName, deploymentLabel, destinationId, destinationLabel, managerKey, profileMetadataReady]);

  const formMetadata = useMemo(
    () => buildDeploymentFormMetadata(metadata, productResourceDefaults),
    [metadata, productResourceDefaults]
  );
  const formFields = useMemo(() => getMetadataFields(formMetadata), [formMetadata]);

  const addDeploymentDraft = useCallback(() => {
    if (!destinationRef.current) return;
    draftSequenceRef.current += 1;
    const draft = createDeploymentDraft(destinationRef.current, productResourceDefaultsRef.current, draftSequenceRef.current);
    const draftKey = getDestinationRowKey(draft);
    pendingDeploymentKeysRef.current.add(String(draftKey));
    onDirty?.();
    setDeployments((previous) => [...previous, draft]);
    setSelectedDeploymentKey(draftKey);
  }, [onDirty]);

  const updateDeployment = useCallback((updatedDeployment) => {
    const incomingKey = String(getDestinationRowKey(updatedDeployment));
    const previousDeployment = deploymentsRef.current.find((deployment, index) =>
      String(getDestinationRowKey(deployment, index)) === incomingKey
    );
    const normalizedUpdate = isSchemaAPISetup(productResourceDefaultsRef.current)
      ? normalizeEndpointRuntimeCapacityUpdate(updatedDeployment, previousDeployment)
      : updatedDeployment;
    const nextDeployment = normalizeDeploymentRow(normalizedUpdate, destinationRef.current, productResourceDefaultsRef.current);
    const updatedKey = String(getDestinationRowKey(nextDeployment));
    pendingDeploymentKeysRef.current.delete(updatedKey);
    onDirty?.();
    setDeployments((previous) => previous.map((deployment, index) =>
      String(getDestinationRowKey(deployment, index)) === updatedKey
        ? nextDeployment
        : deployment
    ));
  }, [onDirty]);

  const cancelDeploymentEdit = useCallback((row, key) => {
    const deploymentKey = String(key || getDestinationRowKey(row));
    if (!pendingDeploymentKeysRef.current.has(deploymentKey)) return;
    pendingDeploymentKeysRef.current.delete(deploymentKey);
    setDeployments((previous) => previous.filter((deployment, index) =>
      String(getDestinationRowKey(deployment, index)) !== deploymentKey
    ));
    setSelectedDeploymentKey("");
  }, []);

  const hardDeleteDeployment = useCallback((row, key) => {
    const activeDeploymentCount = asArray(deploymentsRef.current)
      .filter((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
      .length;
    if (readOnly || activeDeploymentCount <= 1) return;

    const deploymentKey = String(key || getDestinationRowKey(row));
    const deploymentId = String(getDeploymentRecordId(row) || "");
    pendingDeploymentKeysRef.current.delete(deploymentKey);
    onDirty?.();
    setDeployments((previous) => previous.filter((deployment, index) =>
      String(getDestinationRowKey(deployment, index)) !== deploymentKey
    ));
    setSelectedDeploymentKey("");
    setCardEditorOpen(false);

    const currentStatus = connectionStatusRef.current;
    if (currentStatus?.DeploymentId
      && (String(currentStatus.DeploymentId) === deploymentId || String(currentStatus.DeploymentId) === deploymentKey)) {
      updateConnectionStatus({
        Status: "",
        Message: "",
        CheckedUtc: "",
        DeploymentId: "",
        DeploymentName: ""
      });
    }
  }, [onDirty, readOnly, updateConnectionStatus]);

  const saveDeploymentRecords = useCallback(async (currentValues = values, options = {}) => {
    const activeMgr = destinationMgrRef.current;
    const currentDestination = destinationRef.current;
    const resetEntityDeployments = options?.resetEntityDeployments === true;
    if (!currentDestination) throw new Error(`Complete the ${destinationLabel} step before configuring deployments.`);
    if (!formMetadata) throw new Error(`${deploymentLabel} metadata is not available.`);
    const rowsToSave = deploymentsRef.current
      .map((row) => normalizeDeploymentRow(row, currentDestination, productResourceDefaultsRef.current));
    const canonicalRowsToSave = resetEntityDeployments && isSchemaAPISetup(productResourceDefaultsRef.current)
      ? rowsToSave.map((row) => ({
        ...row,
        EntityEndpoints: [],
        ModelEndpoints: [],
        APITokens: []
      }))
      : rowsToSave;
    const validationMessage = validateDeploymentRows(rowsToSave, formFields, deploymentLabel);
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    setLocalError("");
    const destinationToSave = resetEntityDeployments
      ? setEntityDeploymentRows(
          buildDestinationDeploymentSavePayload(currentDestination, productResourceDefaultsRef.current, canonicalRowsToSave),
          []
        )
      : buildDestinationDeploymentSavePayload(currentDestination, productResourceDefaultsRef.current, canonicalRowsToSave);
    const savedDestination = await saveDestination({
      manager: activeMgr,
      destination: {
        ...destinationToSave,
        IsNew: false
      },
      coreState: coreStateRef.current,
      fallbackMessage: `${deploymentLabel} records could not be saved.`
    });
    const returnedRows = getDestinationDeploymentRows(savedDestination, productResourceDefaultsRef.current)
      .map((deployment) => normalizeDeploymentRow(deployment, savedDestination, productResourceDefaultsRef.current));
    const canonicalSavedDestination = resetEntityDeployments
      ? setEntityDeploymentRows(
          buildDestinationDeploymentSavePayload(savedDestination, productResourceDefaultsRef.current, returnedRows),
          []
        )
      : buildDestinationDeploymentSavePayload(
          savedDestination,
          productResourceDefaultsRef.current,
          returnedRows
        );
    const savedRows = getDestinationDeploymentRows(canonicalSavedDestination, productResourceDefaultsRef.current)
      .map((deployment) => normalizeDeploymentRow(deployment, canonicalSavedDestination, productResourceDefaultsRef.current));
    pendingDeploymentKeysRef.current.clear();
    setDestination(canonicalSavedDestination);
    setDeployments(savedRows);
    const stepValues = buildDeploymentStepValues({
      currentValues,
      destination: canonicalSavedDestination,
      deployments: savedRows
    });
    if (resetEntityDeployments) {
      stepValues.EntityDeploymentCount = 0;
      if (isSchemaAPISetup(productResourceDefaultsRef.current)) {
        stepValues.EndpointDeploymentCleanupPrepared = true;
      }
    }
    return { savedDestination: canonicalSavedDestination, savedRows, stepValues };
  }, [deploymentLabel, destinationLabel, formFields, formMetadata, values]);

  const saveDeployments = useCallback(async (currentValues = values) => {
    const { stepValues } = await saveDeploymentRecords(currentValues);
    return stepValues;
  }, [saveDeploymentRecords, values]);

  const updateSelectedEntityObjectIds = useCallback((nextKeys = []) => {
    const normalizedKeys = normalizeEntityObjectSelectionKeys(nextKeys);
    selectedEntityObjectIdsRef.current = normalizedKeys;
    setSelectedEntityObjectIds(normalizedKeys);
  }, []);

  const loadEntityObjectsForScopeSelection = useCallback(async () => {
    const activeEntityObjectMgr = entityObjectMgrRef.current;
    const currentDestination = destinationRef.current;
    const activeSchemaVersionId = readValue(currentDestination, "ActiveSchemaVersionId", "activeSchemaVersionId") || "";

    if (!activeEntityObjectMgr?.Query) throw new Error("Entity Object service is not available.");
    if (!activeSchemaVersionId) return [];

    setEntitySelectorLoading(true);
    try {
      const entityObjectResult = await activeEntityObjectMgr.Query({
        model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
        params: { schemaVersionId: activeSchemaVersionId }
      });
      requireSuccessfulServiceResponse(entityObjectResult, "Entity Objects could not be loaded.", coreStateRef.current);
      const resolvedEntityObjects = getOperationItems(entityObjectResult)
        .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
        .sort((left, right) => getEntityObjectDisplayName(left).localeCompare(getEntityObjectDisplayName(right)));
      const resolvedSourceObjects = await loadSourceObjectSelectData(sourceObjectMgrRef.current);
      entityObjectsRef.current = resolvedEntityObjects;
      setEntityObjects(resolvedEntityObjects);
      setSourceObjectsById(buildSourceObjectsById(resolvedSourceObjects));
      return resolvedEntityObjects;
    } finally {
      setEntitySelectorLoading(false);
    }
  }, []);

  const confirmEntityScopeGeneration = useCallback(async () => {
    if (readOnly || !destinationRef.current || !deploymentsRef.current.length) return true;
    try {
      const preparedDeploymentSave = await saveDeploymentRecords(values, { resetEntityDeployments: true });
      preparedDeploymentSaveRef.current = preparedDeploymentSave;
      const availableEntityObjects = await loadEntityObjectsForScopeSelection();
      if (!availableEntityObjects.length) {
        pendingEntityScopeSelectionRef.current = [];
        return true;
      }

      updateSelectedEntityObjectIds([]);
      setEntitySelectorOpen(true);
      return await new Promise((resolve) => {
        entitySelectorResolverRef.current = resolve;
      });
    } catch (ex) {
      const message = getErrorSummary(ex, "Entity Objects could not be loaded.");
      setLocalError(message);
      preparedDeploymentSaveRef.current = null;
      return false;
    }
  }, [loadEntityObjectsForScopeSelection, readOnly, saveDeploymentRecords, updateSelectedEntityObjectIds, values]);

  const closeEntitySelector = useCallback((confirmed) => {
    const resolver = entitySelectorResolverRef.current;
    entitySelectorResolverRef.current = null;
    if (!confirmed) {
      pendingEntityScopeSelectionRef.current = null;
      preparedDeploymentSaveRef.current = null;
    }
    setEntitySelectorOpen(false);
    updateSelectedEntityObjectIds([]);
    resolver?.(Boolean(confirmed));
  }, [updateSelectedEntityObjectIds]);

  const cancelEntitySelector = useCallback(() => {
    closeEntitySelector(false);
  }, [closeEntitySelector]);

  const generateSelectedEntityScopesOnContinue = useCallback(async (currentValues = values) => {
    const preparedDeploymentSave = preparedDeploymentSaveRef.current;
    preparedDeploymentSaveRef.current = null;
    const { savedDestination, savedRows, stepValues } = preparedDeploymentSave
      || await saveDeploymentRecords(currentValues);
    const selectedIds = Array.isArray(pendingEntityScopeSelectionRef.current)
      ? pendingEntityScopeSelectionRef.current.map(normalizeKey).filter(Boolean)
      : null;
    pendingEntityScopeSelectionRef.current = null;

    if (!selectedIds?.length) return stepValues;

    const defaultDeployment = getDefaultDeployment(savedRows);
    const deploymentId = getDeploymentRecordId(defaultDeployment);
    if (!defaultDeployment || !deploymentId) return stepValues;

    const selectedIdSet = new Set(selectedIds);
    const resolvedEntityObjects = entityObjectsRef.current;
    if (isSchemaAPISetup(productResourceDefaultsRef.current)) {
      const existingPairKeys = new Set(getEndpointEntityRows(defaultDeployment)
        .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
        .map((row) => endpointEntityPairKey(deploymentId, row)));
      const rowsToAdd = [];

      resolvedEntityObjects
        .filter((entityObject) => selectedIdSet.has(normalizeKey(getEntityObjectRecordId(entityObject))))
        .forEach((entityObject) => {
          const entityObjectId = getEntityObjectRecordId(entityObject);
          const nextPairKey = `${deploymentId}:${entityObjectId}`;
          if (!entityObjectId || existingPairKeys.has(nextPairKey)) return;
          rowsToAdd.push({
            IsNew: true,
            IsActive: true,
            IsDeleted: false,
            UniqueId: createClientGuid(),
            SchemaDestinationDeploymentId: deploymentId,
            EntityObjectId: entityObjectId,
            EntityName: readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || getEntityObjectDisplayName(entityObject),
            ExecutionMode: 1,
            EntitySchemaVersionId: readValue(defaultDeployment, "CurrentSchemaVersionId", "currentSchemaVersionId") || "",
            EntitySchemaVersion: readValue(defaultDeployment, "CurrentSchemaVersion", "currentSchemaVersion") || ""
          });
          existingPairKeys.add(nextPairKey);
        });

      if (!rowsToAdd.length) return stepValues;

      const nextRows = savedRows.map((deployment) =>
        String(getDeploymentRecordId(deployment)) === String(deploymentId)
          ? {
            ...deployment,
            EntityEndpoints: [
              ...getEndpointEntityRows(deployment),
              ...rowsToAdd
            ]
          }
          : deployment
      );
      const savedDestinationWithScopes = await saveDestination({
        manager: destinationMgrRef.current,
        destination: {
          ...buildDestinationDeploymentSavePayload(savedDestination, productResourceDefaultsRef.current, nextRows),
          IsNew: false
        },
        coreState: coreStateRef.current,
        fallbackMessage: "Endpoint Scope could not be generated."
      });
      const savedDeployments = getDestinationDeploymentRows(savedDestinationWithScopes, productResourceDefaultsRef.current);
      setDestination(savedDestinationWithScopes);
      setDeployments(savedDeployments);
      return {
        ...stepValues,
        EndpointDeploymentCleanupPrepared: true,
        EntityEndpointCount: savedDeployments
          .flatMap((deployment) => getEndpointEntityRows(deployment))
          .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true).length
      };
    }

    const existingRows = getEntityDeploymentRows(savedDestination)
      .map((row) => normalizeEntityDeploymentRow({
        row,
        entityObjects: resolvedEntityObjects,
        deployments: savedRows,
        defaults: productResourceDefaultsRef.current
      }));
    const existingPairKeys = new Set(existingRows
      .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
      .map(entityDeploymentPairKey));
    const rowsToAdd = [];

    resolvedEntityObjects
      .filter((entityObject) => selectedIdSet.has(normalizeKey(getEntityObjectRecordId(entityObject))))
      .forEach((entityObject) => {
        const entityObjectId = getEntityObjectRecordId(entityObject);
        const nextPairKey = `${deploymentId}:${entityObjectId}`;
        if (!entityObjectId || existingPairKeys.has(nextPairKey)) return;
        draftSequenceRef.current += 1;
        const nextRow = normalizeEntityDeploymentRow({
          row: {
            _tempKey: `destination-entity-deployment-selection-${Date.now()}-${draftSequenceRef.current}`,
            IsNew: true,
            IsActive: true,
            IsDeleted: false,
            UniqueId: createClientGuid(),
            EntityObjectId: entityObjectId,
            EntityDisplayName: getEntityObjectDisplayName(entityObject),
            SchemaDestinationDeploymentId: deploymentId,
            SchemaDestinationName: getDeploymentDisplayName(defaultDeployment, productResourceDefaultsRef.current)
          },
          entityObjects: resolvedEntityObjects,
          deployments: savedRows,
          defaults: productResourceDefaultsRef.current
        });
        rowsToAdd.push(nextRow);
        existingPairKeys.add(nextPairKey);
      });

    if (!rowsToAdd.length) return stepValues;

    const destinationWithCanonicalDeployments = buildDestinationDeploymentSavePayload(
      savedDestination,
      productResourceDefaultsRef.current,
      savedRows
    );
    const savedDestinationWithScopes = await saveDestination({
      manager: destinationMgrRef.current,
      destination: {
        ...setEntityDeploymentRows(destinationWithCanonicalDeployments, [...existingRows, ...rowsToAdd]),
        IsNew: false
      },
      coreState: coreStateRef.current,
      fallbackMessage: "Entity Deployment Scope could not be generated."
    });
    const savedEntityRows = getEntityDeploymentRows(savedDestinationWithScopes);
    setDestination(savedDestinationWithScopes);
    return {
      ...stepValues,
      EntityDeploymentCount: savedEntityRows.filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true).length
    };
  }, [saveDeploymentRecords, values]);

  const confirmSelectedEntityScopes = useCallback(() => {
    pendingEntityScopeSelectionRef.current = selectedEntityObjectIdsRef.current
      .map(normalizeKey)
      .filter(Boolean);
    closeEntitySelector(true);
  }, [closeEntitySelector]);

  const runConnectionCheck = useCallback(async (deployment, rowKey) => {
    const activeMgr = destinationMgrRef.current;
    const defaults = productResourceDefaultsRef.current;
    const operationName = getDestinationCheckOperationName(defaults);
    const requestedDeploymentId = getDeploymentRecordId(deployment);
    const requestedDeploymentKey = String(rowKey || getDestinationRowKey(deployment));
    let operationStarted = false;

    setCheckingDeploymentKey(requestedDeploymentKey);
    setLocalError("");

    try {
      if (!activeMgr?.RunOperation) throw new Error(`${destinationLabel} operation service is not available.`);
      if (!operationName) throw new Error(`Connection check is not configured for this ${destinationLabel}.`);

      const currentDestination = destinationRef.current;
      const currentRows = deploymentsRef.current
        .map((row) => normalizeDeploymentRow(row, currentDestination, defaults));
      const currentDeployment = currentRows.find((row) => String(getDeploymentRecordId(row)) === String(requestedDeploymentId))
        || currentRows.find((row, index) => String(getDestinationRowKey(row, index)) === requestedDeploymentKey)
        || currentRows[0];
      const needsSaveBeforeCheck = !requestedDeploymentId || pendingDeploymentKeysRef.current.has(requestedDeploymentKey);
      const { savedDestination, savedRows } = needsSaveBeforeCheck
        ? await saveDeploymentRecords(values)
        : {
            savedDestination: currentDestination,
            savedRows: currentRows
          };
      const savedDeployment = savedRows.find((row) => String(getDeploymentRecordId(row)) === String(getDeploymentRecordId(currentDeployment)))
        || savedRows.find((row) => String(getDeploymentRecordId(row)) === String(requestedDeploymentId))
        || savedRows.find((row, index) => String(getDestinationRowKey(row, index)) === requestedDeploymentKey)
        || savedRows[0];
      const deploymentId = getDeploymentRecordId(savedDeployment);
      if (!deploymentId) throw new Error(`Save a ${deploymentLabel} before checking the connection.`);

      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: savedDestination,
        queryParams: buildDestinationConnectionCheckMetadata({
          destination: savedDestination,
          deployment: savedDeployment,
          defaults
        })
      });
      operationStarted = true;
      const result = await activeMgr.RunOperation({
        operationName,
        item: savedDestination,
        queryParams,
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      requireSuccessfulServiceResponse(result, "Connection check failed.", coreStateRef.current);
      const operationResult = getOperationResult(result);
      const success = readValue(result, "Success", "success") !== false
        && readValue(operationResult, "Success", "success") !== false;
      if (!success) throw buildServerFailureError(operationResult || result, "Connection check failed.", coreStateRef.current);

      const returnedDeployment = getOperationItem(result);
      if (returnedDeployment && typeof returnedDeployment === "object") {
        setDeployments((previous) => previous.map((row) =>
          String(getDeploymentRecordId(row)) === String(deploymentId)
            ? normalizeDeploymentRow({ ...row, ...returnedDeployment }, savedDestination, defaults)
            : row
        ));
      }

      updateConnectionStatus({
        Status: "Success",
        Message: readValue(operationResult, "DisplayMessage", "displayMessage")
          || readValue(result, "DisplayMessage", "displayMessage")
          || readValue(operationResult, "Value", "value")
          || "Connection check completed.",
        CheckedUtc: new Date().toISOString(),
        DeploymentId: deploymentId,
        DeploymentName: getDeploymentDisplayName(savedDeployment, defaults)
      });
    } catch (ex) {
      const failureStatus = buildConnectionCheckFailureStatus(ex, "Connection check failed.");
      const failureMessage = failureStatus.ConnectionCheckMessage || "Connection check failed.";
      updateConnectionStatus({
        Status: "Failed",
        Message: failureMessage,
        CheckedUtc: new Date().toISOString(),
        DeploymentId: requestedDeploymentId,
        DeploymentName: getDeploymentDisplayName(deployment, defaults)
      });
      setLocalError(operationStarted ? "" : failureMessage);
    } finally {
      setCheckingDeploymentKey("");
    }
  }, [deploymentLabel, destinationLabel, saveDeploymentRecords, updateConnectionStatus, values, wizard]);

  useEffect(() => {
    registerStepController?.({
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: saveDeployments,
      confirmContinue: confirmEntityScopeGeneration,
      prepareContinue: generateSelectedEntityScopesOnContinue
    });
    return () => registerStepController?.(null);
  }, [confirmEntityScopeGeneration, generateSelectedEntityScopesOnContinue, registerStepController, saveDeployments]);

  const deploymentToolbarActions = useMemo(() => (
    readOnly || (isEndpointSetup && deployments.length > 0) ? [] : [{
      Key: "add-destination-deployment",
      Title: "Add Deployment",
      Label: "Add Deployment",
      IconKey: "add",
      Disabled: readOnly || saving || loading || !destination || !formMetadata,
      ActionCallback: addDeploymentDraft
    }]
  ), [addDeploymentDraft, deployments.length, destination, formMetadata, isEndpointSetup, loading, readOnly, saving]);

  const deploymentCardActions = useMemo(() => {
    if (readOnly || !checkOperationName || cardEditorOpen) return [];
    const checking = Boolean(checkingDeploymentKey);
    return [{
      Key: "check-destination-connection",
      Title: "Check Connection",
      DisplayName: checking ? "Checking..." : "Check Connection",
      Disabled: checking || saving || loading || !destination || !formMetadata,
      ActionCallback: runConnectionCheck
    }];
  }, [cardEditorOpen, checkOperationName, checkingDeploymentKey, destination, formMetadata, loading, readOnly, runConnectionCheck, saving]);

  const activeDeploymentCount = useMemo(() => deployments
    .filter((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
    .length, [deployments]);

  const selectedDeploymentHeaderActions = useMemo(() => (
    readOnly ? [] : [{
      Key: "hard-delete-destination-deployment",
      Title: `Delete ${deploymentLabel}`,
      IconKey: "delete",
      Tone: "danger",
      Visible: () => activeDeploymentCount > 1,
      Disabled: saving || loading || Boolean(checkingDeploymentKey),
      ActionCallback: hardDeleteDeployment
    }]
  ), [activeDeploymentCount, checkingDeploymentKey, deploymentLabel, hardDeleteDeployment, loading, readOnly, saving]);

  const deploymentCardTitleResolver = useCallback((row) =>
    getDeploymentCardTitle(row, productResourceDefaultsRef.current), []);

  const deploymentCardFieldVisibleResolver = useCallback((field, row) => {
    if (!isEndpointSetup) return true;
    if (getNormalizedFieldName(field) === "mcpconnectorurl") {
      return isEndpointMcpSurfaceEnabled(row);
    }
    return true;
  }, [isEndpointSetup]);

  const valueDisplayResolvers = useMemo(() => ({
    IsActive: (value) => (value === false ? "Inactive" : "Active"),
    ConnectionString: () => "Configured",
    EnableSync: (value) => (value ? "Enabled" : "Disabled"),
    EnablePreview: (value) => (value ? "Enabled" : "Disabled"),
    [getDeploymentCollectionField(productResourceDefaults)]: (value) => `${asArray(value).length} deployments`
  }), [productResourceDefaults]);

  const entitySelectorRows = useMemo(() => entityObjects.map((entityObject) => {
    const entityObjectId = getEntityObjectRecordId(entityObject);
    return {
      ...entityObject,
      EntitySelectorKey: entityObjectId,
      EntitySelectorName: getEntityObjectDisplayName(entityObject),
      EntitySelectorObject: readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || entityObjectId,
      EntitySelectorSourceObjectOfRecord: getEntityObjectSourceObjectOfRecordDisplayName(entityObject, sourceObjectsById)
    };
  }), [entityObjects, sourceObjectsById]);
  const existingEntityObjectIds = useMemo(() => {
    const rows = isEndpointSetup
      ? deployments.flatMap((deployment) => getEndpointEntityRows(deployment))
      : getEntityDeploymentRows(destination);
    return new Set(rows
      .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
      .map((row) => normalizeKey(readValue(row, "EntityObjectId", "entityObjectId")))
      .filter(Boolean));
  }, [deployments, destination, isEndpointSetup]);
  const selectedEntityObjectIdSet = useMemo(
    () => new Set(selectedEntityObjectIds.map(normalizeKey).filter(Boolean)),
    [selectedEntityObjectIds]
  );
  const availableEntitySelectorIds = useMemo(() => entitySelectorRows
    .filter((row) => !existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row))))
    .map((row) => normalizeKey(getEntityObjectRecordId(row) || row.EntitySelectorKey))
    .filter(Boolean),
  [entitySelectorRows, existingEntityObjectIds]);
  const allAvailableEntitySelectorsSelected = availableEntitySelectorIds.length > 0
    && availableEntitySelectorIds.every((id) => selectedEntityObjectIdSet.has(id));
  const selectAllEntitySelectors = useCallback(() => {
    updateSelectedEntityObjectIds(availableEntitySelectorIds);
  }, [availableEntitySelectorIds, updateSelectedEntityObjectIds]);
  const unselectAllEntitySelectors = useCallback(() => {
    updateSelectedEntityObjectIds([]);
  }, [updateSelectedEntityObjectIds]);
  const entitySelectorToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar">
      <span>{selectedEntityObjectIdSet.size} selected</span>
      <div className="resource-wizard__entity-scope-selector-actions">
        {allAvailableEntitySelectorsSelected ? (
          <ButtonControl
            type="cancel-small"
            onClick={unselectAllEntitySelectors}
            disabled={entitySelectorLoading}
          >
            Unselect All
          </ButtonControl>
        ) : (
          <ButtonControl
            type="cancel-small"
            onClick={selectAllEntitySelectors}
            disabled={entitySelectorLoading || !availableEntitySelectorIds.length}
          >
            Select All
          </ButtonControl>
        )}
      </div>
    </div>
  ), [
    allAvailableEntitySelectorsSelected,
    availableEntitySelectorIds.length,
    entitySelectorLoading,
    selectAllEntitySelectors,
    selectedEntityObjectIdSet.size,
    unselectAllEntitySelectors
  ]);

  const renderCardGrid = () => {
    if (!destinationId) {
      return <div className="resource-wizard__state">Complete the Destination step before configuring deployments.</div>;
    }
    if (loading) {
      return <div className="resource-wizard__linked-step-loading"><Loading /></div>;
    }
    if (!destination) {
      return <div className="resource-wizard__state">The linked {destinationLabel} is not available.</div>;
    }
    if (!formMetadata) {
      return <div className="resource-wizard__state">{deploymentLabel} metadata is not available.</div>;
    }
    return (
      <CardGridControl
        rows={deployments}
        metadata={formMetadata}
        formMetadata={formMetadata}
        formFields={formFields}
        rowKey={getDestinationRowKey}
        selectedRowKey={selectedDeploymentKey}
        onSelectedRowKeyChange={setSelectedDeploymentKey}
        onEditingChange={setCardEditorOpen}
        onCancelEdit={cancelDeploymentEdit}
        onRowChange={updateDeployment}
        toolbarActions={deploymentToolbarActions}
        readOnly={readOnly}
        disabled={saving}
        toggleEnabled={true}
        toggleFieldName="IsActive"
        activeFilterFieldName="IsActive"
        actions={deploymentCardActions}
        cardActionsAlign="end"
        cardTitleResolver={deploymentCardTitleResolver}
        cardFieldVisibleResolver={deploymentCardFieldVisibleResolver}
        selectedHeaderIconActions={selectedDeploymentHeaderActions}
        valueDisplayResolvers={valueDisplayResolvers}
        emptyContent={`No ${deploymentLabel} records are configured yet.`}
        minCardWidth="15rem"
        maxColumns={3}
      />
    );
  };

  const connectionStatusClass = String(connectionStatus.Status || "").trim().toLowerCase();
  const connectionStatusText = connectionStatus.Message
    ? [
      connectionStatus.DeploymentName ? `${connectionStatus.DeploymentName}:` : "",
      connectionStatus.Message
    ].filter(Boolean).join(" ")
    : "";

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title={deploymentLabel}
        description={getDestinationDeploymentStepDescription(productResourceDefaults)}
        countText={`${deployments.length} configured`}
      />
      {requestOnlySetup ? <div className="resource-wizard__state">{requestOnlyReason}</div> : null}
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
        <div className="resource-wizard__linked-record-catalog-scroll">
          {renderCardGrid()}
        </div>
      </div>
      {!cardEditorOpen ? (
        <div className="resource-wizard__linked-step-actions">
          {connectionStatusText ? (
            <div className={`resource-wizard__connection-status ${connectionStatusClass}`}>
              {connectionStatusText}
            </div>
          ) : null}
          <div className="resource-wizard__linked-step-actions-right">
            <ButtonControl
              type="standard-small"
              className="resource-wizard__button resource-wizard__button--secondary"
              onClick={() => onSave?.()}
              disabled={readOnly || saving || loading || Boolean(checkingDeploymentKey) || !destination || !formMetadata}
              loading={saving}
            >
              Save
            </ButtonControl>
            <ButtonControl
              type="ok-small"
              className="resource-wizard__button resource-wizard__button--primary"
              onClick={() => onContinue?.()}
              disabled={readOnly || saving || loading || Boolean(checkingDeploymentKey) || !destination || !formMetadata}
              loading={saving}
            >
              Save & Continue
            </ButtonControl>
          </div>
        </div>
      ) : null}
      <DialogControl
        openDialog={entitySelectorOpen}
        title={isEndpointSetup ? "Generate Endpoint Scope" : "Generate Entity Deployment Scope"}
        okText="Generate Selected"
        cancelText="Cancel"
        onOk={confirmSelectedEntityScopes}
        onCancel={cancelEntitySelector}
        disableOk={!selectedEntityObjectIdSet.size || entitySelectorLoading}
      >
        <div className="resource-wizard__entity-scope-selector">
          {entitySelectorLoading ? (
            <div className="resource-wizard__linked-step-loading"><Loading /></div>
          ) : (
            <CardGridControl
              rows={entitySelectorRows}
              metadata={ENTITY_SELECTOR_METADATA}
              rowKey={(row) => getEntityObjectRecordId(row) || row.EntitySelectorKey}
              selectedRowKeys={selectedEntityObjectIds}
              onSelectedRowKeysChange={updateSelectedEntityObjectIds}
              selectableRowDisabledResolver={(row) => existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row)))}
              selectable={true}
              multiSelect={true}
              headerStatusResolver={getEntitySelectorHeaderStatus}
              activeFilterDefaultMode="both"
              activeFilterOptions={[
                { key: "available", label: "Available" },
                { key: "selected", label: "Selected" },
                { key: "both", label: "Both" }
              ]}
              activeFilterRowPredicate={(row, { mode, isSelected }) => {
                if (mode === "selected") return isSelected;
                if (mode === "both") return true;
                return !existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row)));
              }}
              toolbarLeadingContent={entitySelectorToolbarLeadingContent}
              emptyContent="No Entity Objects are available for the selected active schema version."
              minCardWidth="15rem"
              maxColumns={3}
            />
          )}
        </div>
      </DialogControl>
    </div>
  );
};

export default DestinationDeploymentConfiguratorStep;
